import { SearchFilter } from '@components/Filters/SearchFilter';
import { LoadingIndicator } from '@components/LoadingIndicator';
import useService from '@modules/ServiceModule/useService';
import {
  Form,
  Stack,
  Table,
  TableColumn,
  TableNoRecords,
} from '@servicetitan/design-system';
import { useMemo, useState } from 'react';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import formatEstimatedWaitTime from '@pages/monitoring/QueuesPage/utils/formatEstimatedWaitTime';
import ErrorComponent from '@components/ErrorComponent';
import { OutboundCallEntity } from '@lib/types/common-call-types';
import { OutboundCallDetails } from '../types';
import ServiceModule from '@modules/ServiceModule';

interface IOutboundModalQueuesProps {
  setCallDetails: (details: OutboundCallDetails) => void;
}

const OutboundModalQueues: React.FC<IOutboundModalQueuesProps> = ({
  setCallDetails,
}) => {
  const [queueSearchTerm, setQueueSearchTerm] = useState('');
  const [selectedQueue, setSelectedQueue] = useState('');

  const initialSort: Array<SortDescriptor> = [
    { field: 'estimatedWait', dir: 'asc' },
  ];

  const { queues, loading, userByContactCenterUserId } = useService();
  const [sort, setSort] = useState(initialSort);

  const filteredQueuesData = useMemo(() => {
    const filteredData = Object.values(queues).filter((queue) => {
      return (
        queueSearchTerm.length == 0 ||
        queue.name.toLowerCase().includes(queueSearchTerm.toLowerCase())
      );
    });
    return orderBy(filteredData, sort);
  }, [queueSearchTerm, queues, sort]);

  const pageSize = 4;
  const [page, setPage] = useState({
    skip: 0,
    take: pageSize,
  });

  const processedQueuesData = useMemo(() => {
    return filteredQueuesData
      .slice(page.skip, page.skip + page.take)
      .map((queue) => {
        const availableUsers = queue.memberIds.filter((memberId) => {
          const user = userByContactCenterUserId[memberId];
          return user ? ServiceModule.isUserAvailable(user) : false;
        }).length;

        return {
          ...queue,
          availableUsers, // Injecting the count of available users only
        };
      });
  }, [filteredQueuesData, page.skip, page.take, userByContactCenterUserId]);

  const handlePageChange = (e: { page: { take: number; skip: number } }) => {
    if (page.take !== e.page.take) {
      setPage({ skip: 0, take: e.page.take });
    } else {
      setPage({
        skip: e.page.skip,
        take: e.page.take,
      });
    }
  };

  return (
    <Stack direction='column' spacing={2}>
      <LoadingIndicator show={loading} title='Loading...' />
      <Stack.Item>
        <SearchFilter
          placeholder='Search for a queue'
          className='w-100'
          value={queueSearchTerm}
          onChange={setQueueSearchTerm}
        />
      </Stack.Item>
      <Stack.Item>
        <Table
          borders={{ vertical: false, horizontal: true }}
          loading={loading}
          data={processedQueuesData}
          sortable={true}
          sort={sort}
          onSortChange={(e) => {
            setSort(e.sort);
          }}
          pageable={{ buttonCount: 3 }}
          total={filteredQueuesData.length}
          skip={page.skip}
          pageSize={page.take}
          striped={false}
          onPageChange={handlePageChange}
        >
          <TableNoRecords>
            <div className='ta-center m-x-auto p-y-6' style={{ width: 420 }}>
              <ErrorComponent
                image='block'
                title='No results found'
                body='Try another search.'
              />
            </div>
          </TableNoRecords>
          <TableColumn
            width={40}
            cell={(props) => (
              <td>
                <Form.Radio
                  checked={selectedQueue === props.dataItem.id}
                  label=''
                  onChange={() => {
                    setSelectedQueue(props.dataItem.id);
                    setCallDetails({
                      entity: OutboundCallEntity.Queue,
                      number: props.dataItem.entrypoints?.[0] ?? '',
                      callDisplayName: props.dataItem.name,
                      queueId: props.dataItem.id,
                    });
                  }}
                />
              </td>
            )}
          />
          <TableColumn field='name' title='Queue' />
          <TableColumn title='Employees' field='availableUsers' />
          <TableColumn
            field='status.estimatedWait'
            title='Est. Wait Time'
            cell={(props) => (
              <td>
                {formatEstimatedWaitTime(props.dataItem.status.estimatedWait)}
              </td>
            )}
          />
        </Table>
      </Stack.Item>
    </Stack>
  );
};

export default OutboundModalQueues;
